import React, { memo, useCallback, useState, forwardRef } from "react";
import {
	Button,
	IconButton,
	Popover,
	Typography,
	Switch,
	FormControlLabel,
	Tooltip,
	CircularProgress,
} from "@mui/material";
import {
	Toolbar,
	ToolbarButton,
	QuickFilter,
	QuickFilterControl,
	QuickFilterClear,
	QuickFilterTrigger,
} from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import clsx from "clsx";
import RefreshIcon from "@mui/icons-material/Refresh";

const CustomDataGridToolbar = memo(function CustomDataGridToolbar({
	handleMatchStudents,
	handleOpenDownloadDialog,
	handleChangeGradeWithoutMatching,
	handleOpenClassListCheckDialog,
	loadingClassList,
	allStudentsMatched,
	classListFile,
	examProcessingState,
	reqnum,
	examInDatabase,
	gradeWithoutMatching,
	handleRescoreExam,
}) {
	const [anchorPos, setAnchorPos] = useState(null);

	const onGearClick = useCallback((e) => {
		setAnchorPos({ top: e.clientY + 13, left: e.clientX + 13 });
	}, []);

	const onSwitchChange = useCallback(
		(e, checked) => {
			e.stopPropagation();
			handleChangeGradeWithoutMatching(e, checked);
		},
		[handleChangeGradeWithoutMatching],
	);
	const TextInput = forwardRef(function TextInput(props, ref) {
		return (
			<input
				ref={ref}
				{...props}
				className={clsx(
					"h-9 w-full rounded border border-neutral-200  px-2.5 text-base text-neutral-900  focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary",
					props.className,
				)}
			/>
		);
	});
	const canDownloadAtAll =
		examProcessingState && reqnum === examProcessingState.header?.reqnum && examInDatabase;

	const canDownload = canDownloadAtAll && (allStudentsMatched || gradeWithoutMatching);

	const isScored = examProcessingState?.students?.some(
		(student) =>
			student.items_wrong !== null &&
			student.items_wrong !== undefined &&
			student.items_wrong.length > 0,
	);

	const handleRescore = useCallback(async () => {
		await handleRescoreExam();
	}, [handleRescoreExam]);

	const handleOpenClassDialog = useCallback(() => {
		setAnchorPos(null);
		handleOpenClassListCheckDialog();
	}, [handleOpenClassListCheckDialog]);

	return (
		<Toolbar className="">
			{/* QuickFilter as-is */}
			<QuickFilter
				render={(props, state) => (
					<div {...props} className="mr-auto flex overflow-clip">
						<QuickFilterTrigger
							className={state.expanded ? "rounded-r-none border-r-0" : ""}
							render={
								<ToolbarButton
									render={
										<Button aria-label="Search">
											<SearchIcon fontSize="small" />
										</Button>
									}
								/>
							}
						/>
						<div
							className={clsx(
								"flex overflow-clip transition-all duration-300 ease-in-out",
								state.expanded ? "w-48" : "w-0",
							)}
						>
							<QuickFilterControl
								aria-label="Search"
								placeholder="Search"
								render={({ slotProps, size, ...controlProps }) => (
									<TextInput
										{...controlProps}
										{...slotProps?.htmlInput}
										className={clsx(
											"flex-1 rounded-l-none",
											state.expanded && state.value !== "" && "rounded-r-none",
										)}
									/>
								)}
							/>
							{state.expanded && state.value !== "" && (
								<QuickFilterClear
									render={
										<Button aria-label="Clear" className="rounded-l-none">
											<CancelIcon fontSize="small" />
										</Button>
									}
								/>
							)}
						</div>
					</div>
				)}
			/>

			<div className="relative ">
				{isScored ? (
					<div className=" border-primary text-primary font-semibold py-1 px-2 flex items-center gap-2">
						<div>SCORED</div>
						<Tooltip title="Click to Manually Re-Score Exam" placement="top" arrow>
							<IconButton size="small" onClick={handleRescore} aria-label="Rescore Exam">
								<RefreshIcon color="primary" />
							</IconButton>
						</Tooltip>
					</div>
				) : (
					<div className=" border-gray-500 text-gray-500 font-semibold py-1 px-2 flex items-center gap-2">
						<div>Waiting... Click to Rescore Manually</div>
						<Tooltip title="Click to Manually Re-Score Exam" placement="top" arrow>
							<IconButton size="small" onClick={handleRescore} aria-label="Rescore Exam">
								<RefreshIcon color="gray" />
							</IconButton>
						</Tooltip>
					</div>
				)}
			</div>

			{/* Gear + Popover */}
			<div className="relative">
				<Tooltip title="Advanced Grading Options. Use with caution." placement="top" arrow>
					<span>
						<IconButton
							size="small"
							onClick={onGearClick}
							sx={{ mr: 1 }}
							aria-label="Advanced Grading Options"
						>
							<SettingsOutlinedIcon />
						</IconButton>
					</span>
				</Tooltip>

				<Popover
					open={!!anchorPos}
					onClose={() => setAnchorPos(null)}
					anchorReference="anchorPosition"
					anchorPosition={anchorPos || undefined}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					// keepMounted
					// disableAutoFocus
					// disableEnforceFocus
					// disableRestoreFocus
					slotProps={{
						paper: {
							sx: { p: 2, maxWidth: 300 },
							// onMouseDown: (e) => e.stopPropagation(),
							// onClick: (e) => e.stopPropagation(),
						},
					}}
				>
					<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
						Advanced Options (Use with Caution)
					</Typography>

					<FormControlLabel
						sx={{ display: "flex", gap: 1, m: 0 }}
						control={
							<Switch
								checked={gradeWithoutMatching}
								onChange={onSwitchChange}
								// onClick={(e) => e.stopPropagation()}
								name="gradeWithoutMatching"
								color="primary"
							/>
						}
						label={<Typography variant="body2">Grade Without Matching</Typography>}
					/>
					<Button
						fullWidth
						variant="outlined"
						size="small"
						sx={{ mt: 1 }}
						onClick={handleOpenClassDialog}
					>
						Select Different Class
					</Button>
				</Popover>
			</div>

			<Tooltip
				title="Match students to class list. If students have been previously matched, results can be generated and downloaded based on the old matches."
				placement="top"
			>
				<div>
					<Button
						disabled={classListFile?.classlist.length === 0 || loadingClassList || !classListFile}
						variant={allStudentsMatched ? "outlined" : "contained"}
						onClick={handleMatchStudents}
					>
						{loadingClassList ? (
							<CircularProgress size={24} color="inherit" />
						) : allStudentsMatched ? (
							"Re-Match Students to Class List"
						) : (
							"Match Students to Class List"
						)}
					</Button>
				</div>
			</Tooltip>

			<Button
				onClick={handleOpenDownloadDialog}
				variant="contained"
				color="primary"
				disabled={!canDownload}
			>
				Download/Email Results
			</Button>
		</Toolbar>
	);
});

export default CustomDataGridToolbar;
